// Per-provider Mercado Pago (Marketplace) connection card.
//
// Driven entirely by GET /api/providers/mercadopago/status, so the card tells the truth
// without anyone editing it:
//
//   marketplaceConfigured = false  → the "Próximamente" panel. The platform's OAuth app
//                                    isn't registered yet; nothing the provider does can
//                                    change that, so the button stays genuinely disabled.
//   configured, not connected      → live "Conectar" button.
//   connected                      → calm white card: account id, connected-on, disconnect.
//   needsReconnect                 → amber warning while the provider can still self-serve;
//                                    once the refresh token dies, reconnecting is manual.
//
// The card also reports the outcome of the OAuth round-trip: the API's callback redirects
// to /provider/dashboard?mp=connected|cancelled|error&reason=... We surface that, then strip
// the params so a page refresh doesn't replay a stale banner.
//
// Visual idiom: ocean panel + decorative circles from ProviderCTA.tsx for the action states;
// the standard white dashboard card once there is nothing to act on. Icons are Remixicon.

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import {
  disconnectMercadoPago,
  getMercadoPagoStatus,
  startMercadoPagoConnect,
  type MpConnectionStatus,
} from "@/lib/providerMercadoPago";

export default function MercadoPagoConnectCTA() {
  const { t } = useTranslation(["provider"]);
  const [params, setParams] = useSearchParams();

  const [status, setStatus] = useState<MpConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: "ok" | "warn"; text: string } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getMercadoPagoStatus()
      .then(setStatus)
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Returning from Mercado Pago with the browser's Back button — which is what MP's own
  // cancel does — restores this page from the bfcache with React state intact, including
  // the busy=true set just before navigating away. The button then sits on "Conectando…"
  // until a manual reload. pageshow fires for both a fresh load and a bfcache restore, so
  // clear the flag and re-read status in case they actually completed the connection.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      setBusy(false);
      load();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [load]);

  // Translate the callback's query params into a banner, then clear them.
  useEffect(() => {
    const mp = params.get("mp");
    if (!mp) return;

    if (mp === "connected") {
      setNotice({ kind: "ok", text: t("provider:provider.mpConnected") });
    } else if (mp === "cancelled") {
      setNotice({ kind: "warn", text: t("provider:provider.mpCancelled") });
    } else {
      const reason = params.get("reason") ?? "unknown";
      const known = ["expired_link", "exchange_failed", "account_already_linked", "not_configured", "platform_account"];
      setNotice({
        kind: "warn",
        text: t(`provider:provider.mpErr_${known.includes(reason) ? reason : "unknown"}`),
      });
    }

    const next = new URLSearchParams(params);
    next.delete("mp");
    next.delete("reason");
    setParams(next, { replace: true });
  }, [params, setParams, t]);

  const onConnect = async () => {
    setBusy(true);
    setError(null);
    try {
      const { authorizeUrl } = await startMercadoPagoConnect();
      // Full navigation on purpose: this leaves our origin for Mercado Pago's consent screen.
      window.location.assign(authorizeUrl);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("provider:provider.mpErr_unknown"));
      setBusy(false);
    }
  };

  const onDisconnect = async () => {
    if (!window.confirm(t("provider:provider.mpDisconnectConfirm"))) return;
    setBusy(true);
    setError(null);
    try {
      await disconnectMercadoPago();
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("provider:provider.mpErr_unknown"));
    } finally {
      setBusy(false);
    }
  };

  const banner = notice && (
    <div
      className={`mb-4 rounded-xl px-4 py-3 text-sm flex items-start gap-2 ${
        notice.kind === "ok"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-amber-50 border border-amber-200 text-amber-800"
      }`}
    >
      <i className={`${notice.kind === "ok" ? "ri-check-line" : "ri-error-warning-line"} text-base shrink-0 mt-0.5`} />
      <span>{notice.text}</span>
    </div>
  );

  if (loading) {
    return <div className="h-40 rounded-2xl bg-gray-100 animate-pulse mb-6" aria-hidden />;
  }

  // ---- Connected: nothing to act on, so the card recedes into the dashboard ----
  if (status?.connected && !status.needsReconnect) {
    return (
      <div className="mb-6">
        {banner}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                <i className="ri-bank-card-line text-lg" />
              </div>
              <div>
                <h2 className="text-base font-bold text-charcoal">{t("provider:provider.mpConnectedTitle")}</h2>
                <p className="text-sm text-gray-600 mt-1">{t("provider:provider.mpConnectedBody")}</p>
                <p className="text-xs text-gray-400 mt-2 tabular-nums">
                  {t("provider:provider.mpAccount")}: {status.mpUserId}
                  {status.connectedAt && ` · ${new Date(status.connectedAt).toLocaleDateString("es-MX")}`}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onDisconnect}
              disabled={busy}
              className="text-xs font-medium text-gray-500 hover:text-coral disabled:opacity-50 whitespace-nowrap"
            >
              {busy ? "…" : t("provider:provider.mpDisconnect")}
            </button>
          </div>
          {error && <p className="text-xs text-coral mt-3">{error}</p>}
        </div>
      </div>
    );
  }

  // ---- Action states: ocean panel ----
  const configured = status?.marketplaceConfigured === true;
  const reconnect = status?.needsReconnect === true;

  return (
    <div className="mb-6">
      {banner}
      <div className="relative bg-ocean rounded-2xl overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/4" />

        <div className="relative z-10 p-5 md:p-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-xl bg-white/10 text-white">
              <i className="ri-bank-card-line text-lg" />
            </div>
            {!configured && (
              <span className="inline-flex items-center gap-1.5 bg-sand text-charcoal text-xs font-semibold px-3 py-1 rounded-full">
                <i className="ri-time-line text-[13px]" />
                {t("provider:provider.mpCtaBadge")}
              </span>
            )}
            {configured && reconnect && (
              <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-900 text-xs font-semibold px-3 py-1 rounded-full">
                <i className="ri-error-warning-line text-[13px]" />
                {t("provider:provider.mpReconnectBadge")}
              </span>
            )}
          </div>

          <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
            {reconnect && configured
              ? t("provider:provider.mpReconnectTitle")
              : t("provider:provider.mpCtaTitle")}
          </h2>

          <p className="text-white/85 text-sm leading-relaxed mb-5 max-w-xl">
            {reconnect && configured
              ? t("provider:provider.mpReconnectBody")
              : configured
                ? t("provider:provider.mpCtaBodyLive")
                : t("provider:provider.mpCtaBody")}
          </p>

          <button
            type="button"
            onClick={configured ? onConnect : undefined}
            disabled={!configured || busy}
            aria-disabled={!configured || busy}
            title={configured ? undefined : t("provider:provider.mpCtaSoon")}
            className={
              configured
                ? "inline-flex items-center gap-2 bg-white text-ocean font-semibold px-6 py-3 rounded-full transition-colors hover:bg-white/90 disabled:opacity-60 whitespace-nowrap"
                : "inline-flex items-center gap-2 bg-white/40 text-white/70 font-semibold px-6 py-3 rounded-full cursor-not-allowed select-none whitespace-nowrap"
            }
          >
            <i className={configured ? "ri-link" : "ri-lock-2-line"} />
            {busy
              ? t("provider:provider.mpConnecting")
              : reconnect && configured
                ? t("provider:provider.mpReconnectButton")
                : t("provider:provider.mpCtaButton")}
          </button>

          {!configured && (
            <p className="flex items-center gap-1.5 text-white/60 text-xs mt-3">
              <i className="ri-shield-check-line text-sm" />
              {t("provider:provider.mpCtaSoon")}
            </p>
          )}
          {error && <p className="text-xs text-white bg-coral/80 rounded-lg px-3 py-2 mt-3 inline-block">{error}</p>}
        </div>
      </div>
    </div>
  );
}
